/**
 * H5 参会端 WebSocket：join + liveSpeech 下行 + requestSpeechToken + uplink
 */

import { ref } from 'vue'
import {
  isEnvelopeData,
  liveSpeechToViewerUpdate,
  sessionSnapshotToLanguages,
  shortLangCode,
} from '../adapters/live-speech-adapter'
import { fetchJoinInfo, fetchWsToken } from '../api/cr-v3-viewer.api'
import type {
  ClearSpeechHistoryMessage,
  CrV3SessionSnapshot,
  LiveSpeechEnvelopeMessage,
  LiveSpeechMessage,
  ViewerSubtitleRow,
} from '../types'
import { MAX_DISPLAY_LINES } from '@/modules/speech-translation/constants'
import { applyViewerUpdate } from '../adapters/live-speech-adapter'
import {
  subtitleStyleFromSession,
  subtitleStyleFromUpdateMessage,
} from './subtitle-style-feed'
import type { AudioBusyPayload } from './audio-busy'
import type { CrV3ViewerFeedCallbacks } from './useCrV3ViewerWs'
import {
  resolveViewerSpeakAllowed,
  type CrV3SpeakSessionPatch,
} from '../utils/speak-permission'
import { SpeechTokenCache } from '../utils/speech-token-cache'
import { createWsKeepaliveWorker } from '../utils/cr-v3-ws-keepalive'
import { mergePostClassSessionFields } from '../utils/build-post-class-qr-items'

export interface SpeechTokenResult {
  token: string
  region: string
  expiresAt: number
}

export type RequestSpeechTokenResult =
  | { ok: true; data: SpeechTokenResult }
  | { ok: false; busy: AudioBusyPayload }

export type AcquireFloorResult =
  | { ok: true }
  | { ok: false; busy: AudioBusyPayload }

export interface CrV3SessionLanguagesPatch {
  sourceLanguage: { languageCode: string }
  targetLanguages: Array<{ languageCode: string }>
  classroomWorkLangCode: string
}

export interface CrV3H5WsCallbacks extends CrV3ViewerFeedCallbacks {
  /** lang/select 或 clearSpeechHistory 后刷新 PTT session 语种 */
  onSessionLanguagesChanged?: (langs: CrV3SessionLanguagesPatch) => void
  /** 源语变更 clearSpeechHistory：中断进行中 PTT 以便 SDK 重配 */
  onSourceLanguageReset?: () => void
  onAudioBusy?: (data: AudioBusyPayload) => void
  /** 服务端 audio:stopped（含 floor_idle 释放） */
  onAudioStopped?: (data: { ok?: boolean; reason?: string }) => void
  onActiveSpeakerChanged?: (data: {
    speaking: boolean
    clientId?: string
    name?: string
  }) => void
  onSpeakPermissionChanged?: (allowed: boolean, session: CrV3SpeakSessionPatch) => void
  /** 历史回放前清空 UI 字幕缓冲 */
  onHistoryCleared?: () => void
  /** WS 意外断线（非主动 disconnect） */
  onDisconnected?: (detail: { code: number; reason: string }) => void
  /** WS 重连成功 */
  onReconnected?: () => void
  /** D17：会中 runtime-config（若 Gateway 下发 updateRuntimeConfig） */
  onRuntimeConfigUpdate?: (cfg: { ttsPlaybackRate?: number }) => void
}

export interface UseCrV3H5WsOptions {
  meetingCode: string
  viewerName: string
  /** 入会/PTT 发言语种（join gate 选定，不随收听 tab 变） */
  languageCode: string
  callbacks: CrV3H5WsCallbacks
  tokenCache?: SpeechTokenCache
}

const TOKEN_TIMEOUT_MS = 12_000
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000
const RECONNECT_MAX_ATTEMPTS = 24
/** 非 PTT：配合服务端 30s ping / 90s 超时 */
const IDLE_KEEPALIVE_MS = 25_000
/** PTT 会话：NAT + floor 活性，Worker 定时（主线程可被 SDK 占用） */
const PTT_KEEPALIVE_MS = 4_000

/** H5 WS 客户端：join + downlink + token + uplink JSON + 自动重连 */
export function useCrV3H5Ws(options: UseCrV3H5WsOptions) {
  const connected = ref(false)
  const sttSessionActive = ref(false)
  /** 听众当前收听语种（tab 切换时 changeLanguage） */
  const listenLanguageCode = ref(options.languageCode)

  let ws: WebSocket | null = null
  let intentionalClose = false
  let reconnectAttempt = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null
  let idleKeepaliveTimer: ReturnType<typeof setInterval> | null = null
  let pttKeepaliveTimer: ReturnType<typeof setInterval> | null = null
  let pttKeepaliveWorker: Worker | null = null
  let applyingHistory = false
  let pendingReconnectNotify = false

  const fragments: ViewerSubtitleRow[] = []
  let preview: ViewerSubtitleRow | null = null
  /** 会话快照（课后字段浅合并） */
  let sessionSnapshot: CrV3SessionSnapshot = {}
  const clientIdRef = ref('')
  let speakSession: CrV3SpeakSessionPatch = {}
  const tokenCache = options.tokenCache ?? new SpeechTokenCache()

  function applyHeldSessionSnapshot(patch: CrV3SessionSnapshot, mode: 'merge' | 'replace' = 'merge') {
    sessionSnapshot =
      mode === 'replace'
        ? mergePostClassSessionFields({}, patch)
        : mergePostClassSessionFields(sessionSnapshot, patch)
    options.callbacks.onSessionSnapshot?.(sessionSnapshot)
  }

  let pendingToken:
    | {
        intent: 'stt' | 'tts'
        resolve: (result: RequestSpeechTokenResult) => void
        timer: ReturnType<typeof setTimeout>
      }
    | null = null

  let pendingFloor:
    | {
        resolve: (result: AcquireFloorResult) => void
        timer: ReturnType<typeof setTimeout>
      }
    | null = null

  function clearReconnectTimer() {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer)
      reconnectTimer = null
    }
  }

  function stopIdleKeepalive() {
    if (idleKeepaliveTimer) {
      clearInterval(idleKeepaliveTimer)
      idleKeepaliveTimer = null
    }
  }

  function stopPttKeepaliveInternal() {
    if (pttKeepaliveTimer) {
      clearInterval(pttKeepaliveTimer)
      pttKeepaliveTimer = null
    }
    if (pttKeepaliveWorker) {
      pttKeepaliveWorker.postMessage({ cmd: 'stop' })
      pttKeepaliveWorker.terminate()
      pttKeepaliveWorker = null
    }
  }

  function stopKeepalive() {
    stopIdleKeepalive()
    stopPttKeepaliveInternal()
  }

  /** 非 PTT：标准 pong，维持会中 WS */
  function sendIdleKeepalive() {
    if (ws?.readyState === WebSocket.OPEN) {
      sendJson('pong')
    }
  }

  /** PTT 会话：专用 keepalive，服务端刷新 floor 活性 */
  function sendPttKeepalive() {
    if (ws?.readyState === WebSocket.OPEN) {
      sendJson('pttKeepalive')
    }
  }

  function startIdleKeepalive() {
    stopIdleKeepalive()
    sendIdleKeepalive()
    idleKeepaliveTimer = setInterval(sendIdleKeepalive, IDLE_KEEPALIVE_MS)
  }

  function startPttKeepalive() {
    stopPttKeepaliveInternal()
    stopIdleKeepalive()
    sendPttKeepalive()
    pttKeepaliveWorker = createWsKeepaliveWorker(sendPttKeepalive)
    if (pttKeepaliveWorker) {
      pttKeepaliveWorker.onerror = () => {
        stopPttKeepaliveInternal()
        pttKeepaliveTimer = setInterval(sendPttKeepalive, PTT_KEEPALIVE_MS)
      }
      pttKeepaliveWorker.postMessage({ cmd: 'start', intervalMs: PTT_KEEPALIVE_MS })
    } else {
      pttKeepaliveTimer = setInterval(sendPttKeepalive, PTT_KEEPALIVE_MS)
    }
  }

  function stopPttKeepalive() {
    stopPttKeepaliveInternal()
    if (connected.value && !intentionalClose) {
      startIdleKeepalive()
    }
  }

  /** 入会连接后启动空闲保活（非 PTT） */
  function startKeepalive() {
    startIdleKeepalive()
  }

  function scheduleReconnect() {
    if (intentionalClose || reconnectAttempt >= RECONNECT_MAX_ATTEMPTS) {
      return
    }
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** reconnectAttempt, RECONNECT_MAX_MS)
    reconnectAttempt += 1
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null
      void openSocket(true)
    }, delay)
  }

  function applySpeakSession(session: CrV3SessionSnapshot) {
    speakSession = {
      speakMode: session.speakMode,
      canParticipantSpeak: session.canParticipantSpeak,
      participant: session.participant,
    }
    options.callbacks.onSpeakPermissionChanged?.(
      resolveViewerSpeakAllowed(speakSession, clientIdRef.value),
      speakSession,
    )
  }

  function sendJson(type: string, data: Record<string, unknown> = {}) {
    if (ws?.readyState !== WebSocket.OPEN) {
      return
    }
    ws.send(JSON.stringify({ type, data }))
  }

  function sendAudioStop() {
    sendJson('audio:stop', {})
    sttSessionActive.value = false
  }

  function sendUplinkLiveSpeech(message: LiveSpeechEnvelopeMessage) {
    if (ws?.readyState !== WebSocket.OPEN) {
      return
    }
    ws.send(JSON.stringify(message))
  }

  function applySessionLanguagesFromSnapshot(session: CrV3SessionSnapshot) {
    const langs = sessionSnapshotToLanguages(session)
    options.callbacks.onSessionLanguagesChanged?.({
      sourceLanguage: langs.sourceLanguage,
      targetLanguages: langs.targetLanguages,
      classroomWorkLangCode: langs.classroomWorkLangCode,
    })
    return langs
  }

  function settlePendingToken(result: RequestSpeechTokenResult) {
    if (!pendingToken) {
      return
    }
    clearTimeout(pendingToken.timer)
    pendingToken.resolve(result)
    pendingToken = null
  }

  function settlePendingFloor(result: AcquireFloorResult) {
    if (!pendingFloor) {
      return
    }
    clearTimeout(pendingFloor.timer)
    pendingFloor.resolve(result)
    pendingFloor = null
  }

  function requestSpeechToken(
    intent: 'stt' | 'tts',
    languageCode?: string,
    opts?: { allowCache?: boolean },
  ): Promise<RequestSpeechTokenResult> {
    const allowCache = opts?.allowCache ?? intent === 'tts'
    if (allowCache) {
      const cached = tokenCache.get()
      if (cached) {
        return Promise.resolve({ ok: true, data: cached })
      }
    }
    if (ws?.readyState !== WebSocket.OPEN) {
      return Promise.resolve({
        ok: false,
        busy: { reason: 'not_connected' },
      })
    }
    if (pendingToken) {
      return Promise.resolve({
        ok: false,
        busy: { reason: 'token_in_flight' },
      })
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (pendingToken?.intent === intent) {
          pendingToken = null
          resolve({ ok: false, busy: { reason: 'token_timeout' } })
        }
      }, TOKEN_TIMEOUT_MS)
      pendingToken = { intent, resolve, timer }
      sendJson('requestSpeechToken', {
        intent,
        languageCode: languageCode || options.languageCode,
      })
    })
  }

  function acquireFloor(): Promise<AcquireFloorResult> {
    if (ws?.readyState !== WebSocket.OPEN) {
      return Promise.resolve({
        ok: false,
        busy: { reason: 'not_connected' },
      })
    }
    if (pendingFloor) {
      return Promise.resolve({
        ok: false,
        busy: { reason: 'floor_in_flight' },
      })
    }
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        if (pendingFloor) {
          pendingFloor = null
          sttSessionActive.value = false
          resolve({ ok: false, busy: { reason: 'floor_timeout' } })
        }
      }, TOKEN_TIMEOUT_MS)
      pendingFloor = { resolve, timer }
      sendJson('acquireFloor', {})
    })
  }

  function handleLiveSpeech(payload: LiveSpeechMessage) {
    const update = liveSpeechToViewerUpdate(payload)
    if (!update) {
      return
    }
    const next = applyViewerUpdate({ fragments, preview }, update, MAX_DISPLAY_LINES)
    fragments.length = 0
    fragments.push(...next.fragments)
    preview = next.preview
    if (update.kind === 'preview') {
      options.callbacks.onPreview(preview)
    } else {
      options.callbacks.onFragment(update.row)
      options.callbacks.onPreview(null)
    }
  }

  function clearSubtitleBuffer() {
    fragments.length = 0
    preview = null
    options.callbacks.onHistoryCleared?.()
  }

  function applySpeechHistory(raw: unknown) {
    if (!Array.isArray(raw)) {
      return
    }
    applyingHistory = true
    clearSubtitleBuffer()
    for (const entry of raw) {
      if (!entry || typeof entry !== 'object') {
        continue
      }
      const record = entry as { data?: unknown }
      const envelopeData = isEnvelopeData(record.data) ? record.data : entry
      if (!isEnvelopeData(envelopeData) || (envelopeData.duration ?? 0) < 1) {
        continue
      }
      const update = liveSpeechToViewerUpdate({
        type: 'liveSpeech',
        data: envelopeData,
      })
      if (!update || update.kind !== 'fragment') {
        continue
      }
      const next = applyViewerUpdate({ fragments, preview }, update, MAX_DISPLAY_LINES)
      fragments.length = 0
      fragments.push(...next.fragments)
      preview = next.preview
      options.callbacks.onFragment(update.row)
    }
    options.callbacks.onPreview(null)
    applyingHistory = false
  }

  function handleClearSpeechHistory(_payload: ClearSpeechHistoryMessage) {
    clearSubtitleBuffer()
    options.callbacks.onSourceLanguageReset?.()
    sendJson('getSessionData', {})
  }

  function requestSpeechHistory(languageCode?: string) {
    const lang = languageCode || listenLanguageCode.value || options.languageCode
    sendJson('getSpeechHistory', { languageCode: lang })
  }

  /** 会中切换听众收听语种：changeLanguage → getSpeechHistory */
  async function changeListenLanguage(languageCode: string) {
    const normalized = languageCode.trim()
    if (!normalized || normalized === listenLanguageCode.value) {
      return
    }
    if (ws?.readyState !== WebSocket.OPEN) {
      return
    }
    listenLanguageCode.value = normalized
    sendJson('changeLanguage', { languageCode: normalized })
    requestSpeechHistory(normalized)
  }

  function handleJoinSuccess(data: { clientId?: string }) {
    if (data.clientId) {
      clientIdRef.value = data.clientId
    }
    requestSpeechHistory()
    options.callbacks.onSpeakPermissionChanged?.(
      resolveViewerSpeakAllowed(speakSession, clientIdRef.value),
      speakSession,
    )
    if (pendingReconnectNotify) {
      pendingReconnectNotify = false
      options.callbacks.onReconnected?.()
    }
  }

  function handleMessage(raw: string) {
    let message: { type?: string; data?: unknown }
    try {
      message = JSON.parse(raw)
    } catch {
      return
    }
    const type = message.type

    if (type === 'speechTokenResult') {
      const data = (message.data ?? {}) as SpeechTokenResult
      tokenCache.set(data)
      settlePendingToken({ ok: true, data })
      return
    }

    if (type === 'acquireFloorResult') {
      const data = (message.data ?? {}) as { ok?: boolean }
      if (data.ok) {
        sttSessionActive.value = true
        settlePendingFloor({ ok: true })
      } else {
        sttSessionActive.value = false
        settlePendingFloor({ ok: false, busy: { reason: 'floor_rejected' } })
      }
      return
    }

    if (type === 'liveSpeech') {
      handleLiveSpeech(message as LiveSpeechMessage)
      return
    }
    if (type === 'clearSpeechHistory') {
      handleClearSpeechHistory(message as ClearSpeechHistoryMessage)
      return
    }
    if (type === 'getSessionDataResult') {
      const session = (message.data ?? {}) as CrV3SessionSnapshot
      applyHeldSessionSnapshot(session, 'replace')
      applySpeakSession(session)
      const langs = applySessionLanguagesFromSnapshot(session)
      options.callbacks.onInit({
        fragments: [...fragments],
        preview,
        sourceLanguage: langs.sourceLanguage,
        targetLanguages: langs.targetLanguages,
        classroomWorkLangCode: langs.classroomWorkLangCode,
        subtitleStyle: subtitleStyleFromSession(session),
      })
      return
    }
    if (type === 'updateSubtitleStyle') {
      const style = subtitleStyleFromUpdateMessage(message.data)
      if (style && options.callbacks.onSubtitleStyleUpdate) {
        options.callbacks.onSubtitleStyleUpdate(style)
      }
      return
    }
    if (type === 'joinResult') {
      const data = message.data as { success?: boolean; errorCode?: string; message?: string; clientId?: string }
      if (data?.success) {
        handleJoinSuccess(data)
        sendJson('getSessionData', {})
      } else {
        if (data?.errorCode === 'ROOM_FULL') {
          intentionalClose = true
        }
        options.callbacks.onError(data?.message || 'join failed')
        ws?.close()
      }
      return
    }
    if (type === 'changeLanguageResult') {
      const data = message.data as { success?: boolean; message?: string; languageCode?: string }
      if (data?.success && data.languageCode) {
        listenLanguageCode.value = data.languageCode
      } else if (!data?.success && data?.message) {
        options.callbacks.onError(data.message)
      }
      return
    }
    if (type === 'updateSessionData') {
      const session = (message.data ?? {}) as CrV3SessionSnapshot
      applyHeldSessionSnapshot(session)
      applySpeakSession(session)
      if (
        session.sourceLanguage != null
        || session.targetLanguage != null
        || session.workLangCode != null
        || session.workLangListenCode != null
      ) {
        applySessionLanguagesFromSnapshot(session)
      }
      return
    }
    if (type === 'updateRuntimeConfig') {
      const data = (message.data ?? {}) as { ttsPlaybackRate?: number }
      options.callbacks.onRuntimeConfigUpdate?.(data)
      return
    }
    if (type === 'getSpeechHistoryResult') {
      applySpeechHistory(message.data)
      return
    }
    if (type === 'audio:stopped') {
      sttSessionActive.value = false
      const data = (message.data ?? {}) as { ok?: boolean; reason?: string }
      options.callbacks.onAudioStopped?.(data)
      return
    }
    if (type === 'audio:busy') {
      sttSessionActive.value = false
      const data = (message.data ?? {}) as AudioBusyPayload
      if (pendingFloor) {
        settlePendingFloor({ ok: false, busy: data })
      }
      if (pendingToken) {
        settlePendingToken({ ok: false, busy: data })
      }
      options.callbacks.onAudioBusy?.(data)
      return
    }
    if (type === 'activeSpeakerChanged') {
      const data = (message.data ?? {}) as {
        speaking?: boolean
        clientId?: string
        name?: string
      }
      options.callbacks.onActiveSpeakerChanged?.({
        speaking: Boolean(data.speaking),
        clientId: data.clientId,
        name: data.name,
      })
      return
    }
    if (type === 'error') {
      const data = (message.data ?? {}) as { message?: string; code?: number }
      if (data.message) {
        options.callbacks.onError(data.message)
      }
      return
    }
    if (type === 'ping') {
      sendJson('pong')
    }
  }

  function attachSocketHandlers(socket: WebSocket, isReconnect: boolean) {
    socket.onopen = () => {
      connected.value = true
      pendingReconnectNotify = isReconnect
      reconnectAttempt = 0
      startKeepalive()
      options.callbacks.onLoading(false)
      const joinPayload: Record<string, unknown> = {
        name: options.viewerName,
        languageCode: options.languageCode,
        role: 'viewer',
      }
      if (clientIdRef.value) {
        joinPayload.clientId = clientIdRef.value
      }
      sendJson('join', joinPayload)
      // getSessionData 在 joinResult.success 后发送（D12）
    }

    socket.onmessage = (ev) => {
      if (typeof ev.data === 'string') {
        handleMessage(ev.data)
      }
    }

    socket.onerror = () => {
      options.callbacks.onError('WebSocket 连接异常')
    }

    socket.onclose = (ev) => {
      connected.value = false
      sttSessionActive.value = false
      stopKeepalive()
      options.callbacks.onLoading(false)
      if (!intentionalClose) {
        options.callbacks.onDisconnected?.({
          code: ev.code,
          reason: ev.reason || '',
        })
        scheduleReconnect()
      }
    }
  }

  async function openSocket(isReconnect = false) {
    if (!isReconnect) {
      options.callbacks.onLoading(true)
    }
    try {
      if (!isReconnect) {
        const joinInfo = await fetchJoinInfo(options.meetingCode)
        if (joinInfo.remainingSlots <= 0 && !clientIdRef.value) {
          options.callbacks.onError('会议人数已满')
          options.callbacks.onLoading(false)
          return
        }
      }

      const tokenData = await fetchWsToken(options.meetingCode)
      const url = `${tokenData.wsUrl}?token=${encodeURIComponent(tokenData.token)}`

      if (ws) {
        ws.onclose = null
        ws.close()
      }

      ws = new WebSocket(url)
      attachSocketHandlers(ws, isReconnect)
    } catch (err) {
      options.callbacks.onLoading(false)
      if (!intentionalClose) {
        scheduleReconnect()
      }
      if (!isReconnect) {
        options.callbacks.onError(err instanceof Error ? err.message : '连接失败')
      }
    }
  }

  async function connect() {
    intentionalClose = false
    clearReconnectTimer()
    reconnectAttempt = 0
    await openSocket(false)
  }

  function disconnect() {
    intentionalClose = true
    clearReconnectTimer()
    stopKeepalive()
    if (sttSessionActive.value) {
      sendAudioStop()
    }
    if (ws) {
      ws.onclose = null
      ws.close()
      ws = null
    }
    connected.value = false
    sttSessionActive.value = false
    tokenCache.clear()
  }

  return {
    connect,
    disconnect,
    connected,
    clientId: clientIdRef,
    listenLanguageCode,
    sttSessionActive,
    sendAudioStop,
    sendIdleKeepalive,
    startPttKeepalive,
    stopPttKeepalive,
    sendUplinkLiveSpeech,
    requestSpeechToken,
    acquireFloor,
    changeListenLanguage,
    tokenCache,
    /** 历史回放中跳过 TTS，避免 join 后 token 风暴 */
    get applyingHistory() {
      return applyingHistory
    },
  }
}
